import React, { useState, useCallback, useMemo } from "react";
import { DatePicker } from "antd";
import Slider from "../components/Slider";
import moment from "moment";
import DurationInput from "../components/DurationInput";
import styled from "styled-components";
import { Fields } from "../components/GoalAmountFormInputs";
import {
  SliderTitle,
  SliderTitleProps,
  StyledSubtitle,
} from "../components/SliderTitle";
import { useDispatch } from "react-redux";
import { UpdateFieldProps } from "src/store/Goal-Based/goal";
import { colors } from "src/theme/colors";
import { line, radii, space, withAlpha } from "src/theme/ui";

const MAX_YEARS = 30;
const MAX_TOTAL_MONTHS = MAX_YEARS * 12;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledDurationSliderInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.lg}px;

  @media (max-width: 570px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: ${space.sm}px;
  }
`;

/* The ends of the range, so a handle against its left stop means something.
   Six months is the floor the API accepts, thirty years the ceiling. */
const Bounds = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -2px;

  span {
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: ${colors.gray[400]};
  }
`;

const Track = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledDates = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${space.lg}px;
  margin-top: 4px;

  @media (max-width: 570px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${space.sm}px;
  }
`;

export const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 34px;
  border-radius: ${radii.control}px;
  border-color: ${line.hairline};
  background-color: ${colors.white};
  box-shadow: none;

  &:hover {
    border-color: ${colors.gray[300]};
  }

  &.ant-picker-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  input {
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }
`;

/**
 * When it finishes — stated, not offered.
 *
 * This was a second date picker with `disabled` on it, which draws a control
 * and then refuses the click it invites. The end date is not a field: it is
 * the start date plus the duration, so there is nothing here for a reader to
 * decide and nothing to type. It reads as the derived fact it is.
 */
const EndsOn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: none;

  b {
    height: 34px;
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }
`;

export const StyledDate = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const titles: SliderTitleProps = {
  title: "Duration",
};

export default function TimeDurationInput<T>(props: UpdateFieldProps<T>) {
  const { updateField, value, startDate: initialStartDate } = props;
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(moment(initialStartDate));
  const [years, setYears] = useState(Math.floor(Number(value) / 12));
  const [months, setMonths] = useState(Number(value) % 12);
  const [duration, setDuration] = useState(Number(value));

  const endDate = useMemo(() => {
    if (startDate) {
      const date = moment(startDate).add({ years, months });
      dispatch(
        updateField({
          name: Fields.endDate,
          value: date.format("YYYY-MM-DDTHH:mm:ss.SSSSSS"),
        }),
      );
      return date;
    }
    return null;
  }, [startDate, years, months]);

  const disabledDate = useCallback((current) => {
    return current && current < moment().startOf("day");
  }, []);

  const handleStartDateChange = useCallback((date) => {
    setStartDate(date);
    dispatch(
      updateField({
        name: Fields.startDate,
        value: date.format("YYYY-MM-DDTHH:mm:ss.SSSSSS"),
      }),
    );
  }, []);

  const handleYearsChange = useCallback(
    (value) => {
      setYears(value);
      setDuration(value * 12 + months);
      dispatch(
        updateField({ name: Fields.period, value: value * 12 + months }),
      );
    },
    [months],
  );

  const handleMonthsChange = useCallback(
    (value) => {
      setMonths(value);
      setDuration(value + years * 12);
      dispatch(updateField({ name: Fields.period, value: value + years * 12 }));
    },
    [years],
  );

  const handleSliderChange = useCallback((value) => {
    dispatch(updateField({ name: Fields.period, value: value }));
    setYears(Math.floor(value / 12));
    setMonths(value % 12);
  }, []);

  return (
    <StyledContainer>
      <SliderTitle {...titles} />
      <StyledDurationSliderInput>
        <Track>
          <Slider
            min={6}
            name={Fields.period}
            max={MAX_TOTAL_MONTHS}
            value={duration}
            tooltipVisible={false}
            onAfterChange={handleSliderChange}
            onChange={setDuration}
          />
          <Bounds>
            <span>6 mo</span>
            <span>{MAX_YEARS} yrs</span>
          </Bounds>
        </Track>
        <DurationInput
          years={Math.floor(duration / 12)}
          months={duration % 12}
          handleMonthsChange={handleMonthsChange}
          handleYearsChange={handleYearsChange}
        />
      </StyledDurationSliderInput>
      <StyledDates>
        <StyledDate>
          <StyledSubtitle>Starts</StyledSubtitle>
          <StyledDatePicker
            value={startDate}
            onChange={handleStartDateChange}
            disabledDate={disabledDate}
            allowClear={false}
          />
        </StyledDate>
        <EndsOn>
          <StyledSubtitle>Ends</StyledSubtitle>
          <b>{endDate ? endDate.format("D MMM YYYY") : "—"}</b>
        </EndsOn>
      </StyledDates>
    </StyledContainer>
  );
}
